import axios, { AxiosError, AxiosRequestConfig } from "axios";
import userInfoService from "@/util/userInfoService";
import Vue from "vue";
 
const getCognitoConfig = () => {
    const cognitoLoginUrl = Vue?.prototype?.$config?.VITE_APP_COGNITO_LOGIN_URL ??
        Vue?.prototype?.$config?.COGNITO_LOGIN_URL;
    const clientId = Vue?.prototype?.$config?.VITE_APP_COGNITO_CLIENT_ID ??
        Vue?.prototype?.$config?.COGNITO_CLIENT_ID;
   
    console.log('Cognito config:', { cognitoLoginUrl, clientId, config: Vue?.prototype?.$config });
   
    if (!cognitoLoginUrl || !clientId) {
        throw new Error('Cognito configuration is missing. Please configure VITE_APP_COGNITO_LOGIN_URL and VITE_APP_COGNITO_CLIENT_ID in appConfig.json');
    }
   
    return { cognitoLoginUrl, clientId };
};
 
const sha256 = async (str: string): Promise<ArrayBuffer> => {
    return await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
};
 
const generateNonce = async (): Promise<string> => {
    const randomValues = crypto.getRandomValues(new Uint32Array(4));
    const hash = await sha256(randomValues.toString());
    // https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
    const hashArray = Array.from(new Uint8Array(hash));
    return hashArray.map(byte => byte.toString(16).padStart(2, "0")).join("");
};
 
const base64URLEncode = (buffer: ArrayBuffer): string => {
    const byteArray = new Uint8Array(buffer);
    const byteString = String.fromCharCode(...byteArray);
    return btoa(byteString)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
};
 
const redirectToPrevious = (): void => {
    const url = sessionStorage.getItem('redirect-url');
    if (url !== null) window.location.assign(url);
    sessionStorage.removeItem('redirect-url');
}
 
const redirectToLogin = async (): Promise<void> => {
    const { cognitoLoginUrl, clientId } = getCognitoConfig();
    const state = await generateNonce();
    const codeVerifier = await generateNonce();
    sessionStorage.setItem(`codeVerifier-${state}`, codeVerifier);
    const codeChallenge = base64URLEncode(await sha256(codeVerifier));
    sessionStorage.setItem('redirect-url', window.location.href);
    window.location.assign(`${cognitoLoginUrl}/login?response_type=code&client_id=${clientId}&redirect_uri=${window.location.origin}&state=${state}&code_challenge_method=S256&code_challenge=${codeChallenge}`);
};
 
const fetchToken = async (searchParams: URLSearchParams): Promise<any> => {
    const { cognitoLoginUrl, clientId } = getCognitoConfig();
    const state = searchParams.get("state");
    const codeVerifier = sessionStorage.getItem(`codeVerifier-${state}`);
    const tokenString = localStorage.getItem("tokens");
    let refreshToken = "";
    if (tokenString) {
        const tokens = JSON.parse(tokenString);
        refreshToken = tokens.refresh_token;
    }
    const res = await fetch(`${cognitoLoginUrl}/oauth2/token`, {
        method: "POST",
        headers: new Headers({ "content-type": "application/x-www-form-urlencoded" }),
        body: Object.entries({
            "grant_type": "authorization_code",
            "client_id": clientId,
            "code": searchParams.get("code"),
            "code_verifier": codeVerifier,
            "redirect_uri": window.location.origin,
            "refresh_token": refreshToken,
        }).map(([k, v]) => `${k}=${v}`).join("&"),
    });
    const tokens = await res.json();
    console.log("res: ", res);
    if (!res?.ok) {
        throw new Error(tokens);
    }
    localStorage.setItem("tokens", JSON.stringify(tokens));
    axios.defaults.headers.Authorization = `Bearer ${tokens.id_token}`;
    sessionStorage.removeItem(`codeVerifier-${state}`);
    
    // Trigger group claims sync after successful token exchange
    triggerGroupClaimsSync();
    
    redirectToPrevious();
    return await tokens;
};
 
const fullFetch = async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    const searchParams = new URLSearchParams(window.location.search);
 
    if (searchParams.get("code")) {
        const tokens = await fetchToken(searchParams);
        if (!config.headers) config.headers = {};
        config.headers.Authorization = `Bearer ${tokens.id_token}`;
        // Note: fetchToken already triggers group claims sync
        return config;
    }
 
    const storedToken = localStorage.getItem("tokens");
 
    if (!storedToken) {
        await redirectToLogin();
        return config;
    }
 
    const tokens = JSON.parse(storedToken);
    axios.defaults.headers.Authorization = `Bearer ${tokens.id_token}`;
    if (config?.headers) {
        config.headers.Authorization = `Bearer ${tokens.id_token}`;
    }
    return config;
};
 
export const getToken = async (): Promise<string | undefined> => {
    const searchParams = new URLSearchParams(window.location.search);
 
    if (searchParams.get("code")) {
        const tokens = await fetchToken(searchParams);
        // Note: fetchToken already triggers group claims sync
        return tokens.id_token;
    }
 
    const storedToken = localStorage.getItem("tokens");
 
    if (!storedToken) {
        return;
    }
 
    const tokens = JSON.parse(storedToken);
    return tokens.id_token;
};
 
const errorHandler = (err: AxiosError): Promise<AxiosRequestConfig> => {
    if (err?.response?.status == 401) {
        localStorage.removeItem("tokens");
        return fullFetch(err.response.config);
    }
    return Promise.reject(err);
};
 
const requestHandler = async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    // if the url is using our base url and contains the '/api'
    // again we trim it off since it's included in the base url
    if (config.url?.startsWith('/api')) {
        config.url = config.url.slice(4);
    } else if (config.url?.startsWith('api')) {
        config.url = config.url.slice(3);
    }
 
    await userInfoService.fetchUserInfoIfNeeded(config.url);
   
    const hasAuthHeader = config?.headers?.Authorization &&
        config.headers.Authorization != "Bearer " &&
        config.headers.Authorization != "Bearer Unknown";
 
    if (hasAuthHeader) return config;
 
    const searchParams = new URLSearchParams(window.location.search);
 
    const code = searchParams.get("code");
    if (code) {
        const tokens = await fetchToken(searchParams);
        if (!config.headers) config.headers = {};
        config.headers.Authorization = `Bearer ${tokens.id_token}`;
        // Note: fetchToken already triggers group claims sync
        return config;
    }
 
    const storedToken = localStorage.getItem("tokens");
 
    if (!storedToken) {
        await redirectToLogin();
        return config;
    }
 
    const tokens = JSON.parse(storedToken);
    axios.defaults.headers.Authorization = `Bearer ${tokens.id_token}`;
    if (config?.headers) {
        config.headers.Authorization = `Bearer ${tokens.id_token}`;
    }
 
    return config;
};
 
export function setupCognito(): void {
    axios.interceptors.request.use(requestHandler);
    axios.interceptors.response.use(
        response => response,
        (err: AxiosError) => errorHandler(err)
    );
}
 
const triggerGroupClaimsSync = async (): Promise<any> => {
    try {
        console.log("Triggering group claims sync...");
        const response = await axios.post("/User/sync-group-claims");
        console.log("Group claims sync response:", response.data);
        return response.data;
    } catch (error) {
        console.warn("Group claims sync failed:", error);
        // Don't throw - this shouldn't block authentication
        return { success: false, error };
    }
};

export function logoutCognito(): void {
    const { cognitoLoginUrl, clientId } = getCognitoConfig();
    localStorage.removeItem("tokens");
    window.location.assign(`${cognitoLoginUrl}/logout?response_type=code&client_id=${clientId}&redirect_uri=${window.location.origin}`);
}